<template>
  <section class="cashier-pending-wrapper">
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col md="12">
            <div class="d-flex align-items-center justify-content-between">
              <h2>{{ $t("cashiers.pending-title") }}</h2>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="$router.push({ name: 'cashiers-pending' })"
              >
                <feather-icon
                  icon="ArrowLeftIcon"
                  class="mr-1"
                  size="14"
                />
                {{ $t("cashiers.back-to-list") }}
              </b-button>
            </div>
          </b-col>
          <b-col
            cols="12"
            md="12"
          >
            <br>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.date')"
              label-for="filter-date"
            >
              <datepicker
                id="filter-date"
                v-model="filterDate"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.type')"
              label-for="type-filter"
            >
              <v-select
                id="type-filter"
                v-model="filterType"
                :options="invoiceTypeOptions"
                label="text"
                :reduce="(option) => option.value"
                :clearable="true"
                :placeholder="$t('common.type')"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              label="نوع الخدمة"
              label-for="service-type-filter"
            >
              <v-select
                id="service-type-filter"
                v-model="filterServiceType"
                :options="serviceTypeFilterOptions"
                label="text"
                :reduce="(option) => option.value"
                :clearable="true"
                placeholder="نوع الخدمة"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              class="mt-50"
              variant="primary"
              @click="fetchPendingInvoices()"
            >
              {{ $t("common.search") }}
              <feather-icon
                icon="SearchIcon"
                class="ml-1"
                size="16"
              />
            </b-button>
          </b-col>
        </b-row>

        <b-row
          v-if="invoices.length > 0"
          class="mt-1"
        >
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-end"
          >
            <b-button
              class="mt-50"
              variant="success"
              @click="showApproveModal = true"
            >
              {{ $t("cashiers.bulk-approve-all") }}
              <feather-icon
                icon="CheckIcon"
                class="ml-1"
                size="16"
              />
            </b-button>
          </b-col>
        </b-row>

        <!-- Summary Section -->
        <b-row v-if="hasSummaryData">
          <b-col cols="12">
            <b-card class="summary-card">
              <h5 class="mb-1">
                {{ $t("cashiers.summary") }}
              </h5>
              <b-row class="summary-row d-flex flex-wrap justify-content-between">
                <b-col
                  md="2"
                  cols="6"
                >
                  <div class="summary-item">
                    <span class="summary-label">{{
                      $t("cashiers.total-invoices")
                    }}</span>
                    <span class="summary-value">{{ sums.count }}</span>
                  </div>
                </b-col>
                <b-col
                  md="2"
                  cols="6"
                >
                  <div class="summary-item">
                    <span class="summary-label">{{
                      $t("cashiers.total-amount")
                    }}</span>
                    <span class="summary-value">{{
                      formatAmount(sums.total_amount, "IQD")
                    }}</span>
                  </div>
                </b-col>
                <b-col
                  md="2"
                  cols="6"
                >
                  <div class="summary-item">
                    <span class="summary-label">{{
                      $t("cashiers.total-discount")
                    }}</span>
                    <span class="summary-value">{{
                      formatAmount(sums.total_discount, "IQD")
                    }}</span>
                  </div>
                </b-col>
                <b-col
                  md="2"
                  cols="6"
                >
                  <div class="summary-item">
                    <span class="summary-label">
                      الاسترداد
                    </span>
                    <span class="summary-value refund-amount">{{
                      formatAmount(refunds.total_refund_amount, "IQD")
                    }}</span>
                    <small class="text-muted d-block">
                      {{ refunds.refund_count }} {{ $t("common.total-invoices") || "invoice(s)" }}
                    </small>
                  </div>
                </b-col>
                <b-col
                  md="2"
                  cols="6"
                >
                  <div class="summary-item">
                    <span class="summary-label">{{
                      $t("cashiers.net-amount")
                    }}</span>
                    <span class="summary-value net-amount">{{
                      formatAmount(adjustedNetAmount, "IQD")
                    }}</span>
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-table
              v-if="!loading"
              :items="preparedInvoices"
              responsive
              :fields="tableColumns"
              primary-key="invoiceId"
              class="position-relative"
              show-empty
              :empty-text="$t('cashiers.no-pending-invoices')"
            >
              <template #cell(type)="data">
                <div>
                  <span class="text-nowrap">{{
                    formatInvoiceType(data.item.type)
                  }}</span>
                  <div
                    v-if="
                      data.item.serviceTitle || data.item.serviceTypeName
                    "
                    class=" mt-1"
                  >
                    <span v-if="data.item.serviceTitle">{{
                      data.item.serviceTitle
                    }}</span>
                    <span
                      v-if="
                        data.item.serviceTitle && data.item.serviceTypeName
                      "
                    >
                      ·
                    </span>
                    <span v-if="data.item.serviceTypeName">{{
                      data.item.serviceTypeName
                    }}</span>
                  </div>
                </div>
              </template>
              <template #cell(number)="data">
                <span class="text-nowrap">{{ data.item.number }}</span>
              </template>
              <template #cell(patientName)="data">
                <span>{{ data.item.patientName }}</span>
              </template>
              <template #cell(itemTitle)="data">
                <span>{{ data.item.itemTitle }}</span>
              </template>
              <template #cell(amount)="data">
                <span class="font-weight-bold">{{
                  formatAmount(data.item.amount, data.item.currency)
                }}</span>
              </template>
              <template #cell(discount)="data">
                <span>{{
                  formatAmount(data.item.discount || 0, data.item.currency)
                }}</span>
              </template>
              <template #cell(status)="data">
                <b-badge variant="warning">
                  {{ data.item.status }}
                </b-badge>
              </template>
              <template #cell(paidBy)="data">
                <div v-if="data.item.paidBy">
                  <div class="font-weight-bold">
                    {{ data.item.paidBy.name || "-" }}
                  </div>
                </div>
                <span v-else>-</span>
              </template>
              <template #cell(paidDate)="data">
                <span class="text-nowrap">{{
                  formatDate(data.item.paidDate)
                }}</span>
              </template>
              <template #cell(createdBy)="data">
                <span>{{ data.item.createdBy || "-" }}</span>
              </template>
              <template #cell(createdAt)="data">
                <span class="text-nowrap">{{
                  formatDate(data.item.createdAt)
                }}</span>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
          </b-col>
        </b-row>
      </div>
      <div
        v-if="pagination.total > 0"
        class="mx-2 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted mr-2">
              {{ $t("common.showing") }}
              {{
                pagination.from ||
                  (pagination.page - 1) * pagination.pageSize + 1
              }}
              {{ $t("common.to") }}
              {{
                pagination.to ||
                  Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.total
                  )
              }}
              {{ $t("common.of") || "of" }} {{ pagination.total }}
            </span>
            <b-form-select
              v-model="pagination.pageSize"
              :options="pageSizeOptions"
              size="sm"
              style="width: auto; min-width: 80px"
              class="ml-2"
              @change="onPageSizeChange"
            />
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.total"
              :per-page="pagination.pageSize"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Bulk Approve Modal -->
    <b-modal
      v-model="showApproveModal"
      :title="$t('cashiers.bulk-approve-title')"
      :ok-title="$t('common.ok')"
      ok-variant="success"
      :cancel-title="$t('common.cancel')"
      @ok="handleBulkApprove"
      @cancel="approveNote = ''"
    >
      <b-form-group
        :label="$t('cashiers.approval-note')"
        label-for="approve-note"
      >
        <b-form-textarea
          id="approve-note"
          v-model="approveNote"
          rows="3"
          placeholder="Enter a note for this approval..."
        />
      </b-form-group>
      <p class="text-muted">
        This will approve all {{ invoices.length }} pending invoices for this
        cashier.
      </p>
    </b-modal>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BButton,
  BFormGroup,
  BFormSelect,
  BPagination,
  BSpinner,
  BModal,
  BFormTextarea,
  BBadge,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const INVOICE_TYPE_MAP = {
  invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
  invoice_nicu: 'cashiers.invoice-type-nicu',
  invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
  refund: 'cashiers.invoice-type-refund',
  invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
  invoice_emergency: 'cashiers.invoice-type-emergency',
}

export default {
  name: 'CashierPending',
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BButton,
    BFormGroup,
    BFormSelect,
    BPagination,
    BSpinner,
    BModal,
    BFormTextarea,
    BBadge,
    Datepicker,
    vSelect,
  },
  data() {
    return {
      cashierId: null,
      filterDate: null,
      filterType: null,
      filterServiceType: null,
      serviceTypeOptions: [],
      showApproveModal: false,
      approveNote: '',
      loading: false,
      invoices: [],
      sums: {
        total_amount: 0,
        total_discount: 0,
        net_amount: 0,
        count: 0,
      },
      refunds: {
        total_refund_amount: 0,
        refund_count: 0,
      },
      pagination: {
        page: 1,
        pageSize: 15,
        total: 0,
        from: null,
        to: null,
      },
      pageSizeOptions: [
        { value: 10, text: '10' },
        { value: 15, text: '15' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    }
  },
  computed: {
    tableColumns() {
      return [
        { key: 'type', label: this.$t('common.type'), sortable: true },
        {
          key: 'number',
          label: this.$t('common.invoice-number'),
          sortable: true,
        },

        {
          key: 'patientName',
          label: this.$t('reception.patient-name'),
          sortable: false,
        },
        {
          key: 'itemTitle',
          label: this.$t('hospital.service'),
          sortable: false,
        },
        { key: 'amount', label: this.$t('common.amount'), sortable: true },
        { key: 'discount', label: this.$t('common.discount'), sortable: true },
        // { key: "status", label: this.$t("common.status"), sortable: true },
        {
          key: 'paidBy',
          label: 'القابض',
          sortable: false,
        },
        {
          key: 'paidDate',
          label: 'تاريخ الاستلام',
          sortable: true,
        },
        {
          key: 'createdBy',
          label: 'المنشئ',
          sortable: false,
        },
        {
          key: 'createdAt',
          label: 'تاريخ الإنشاء',
          sortable: true,
        },
      ]
    },
    invoiceTypeOptions() {
      const options = [{ value: null, text: this.$t('common.all') }]
      Object.keys(INVOICE_TYPE_MAP).forEach(key => {
        options.push({
          value: key,
          text: this.formatInvoiceType(key),
        })
      })
      return options
    },
    serviceTypeFilterOptions() {
      const options = [{ value: null, text: this.$t('common.all') }]
      this.serviceTypeOptions.forEach(st => {
        options.push({
          value: st.id,
          text: st.name,
        })
      })
      return options
    },
    preparedInvoices() {
      if (!Array.isArray(this.invoices)) return []
      return this.invoices.map(raw => ({
        invoiceId: raw.id,
        type: raw.type,
        number: raw.invoice_no,
        patientName:
          (raw.patientCard
            && raw.patientCard.patient
            && raw.patientCard.patient.name)
          || (raw.patient_card
            && raw.patient_card.patient
            && raw.patient_card.patient.name)
          || '-',
        itemTitle:
          (raw.service && raw.service.title)
          || (raw.surgery && raw.surgery.title)
          || '-',
        serviceTitle:
          (raw.service && raw.service.title) || null,
        serviceTypeName:
          (raw.service
            && raw.service.type
            && raw.service.type.name)
          || null,
        amount: raw.amount,
        discount: raw.discount || 0,
        currency: raw.currency || 'IQD',
        status: raw.status,
        paidBy: raw.approved_by || null,
        paidDate: raw.approved_at || null,
        createdAt: raw.created_at,
        createdBy:
          (raw.created_by && raw.created_by.name)
          || (raw.createdBy && raw.createdBy.name)
          || '-',
      }))
    },
    adjustedNetAmount() {
      return Number(this.sums.net_amount || 0) - Number(this.refunds.total_refund_amount || 0)
    },
    hasSummaryData() {
      return Number(this.sums.count || 0) > 0
        || Number(this.refunds.refund_count || 0) > 0
        || Number(this.refunds.total_refund_amount || 0) > 0
    },
  },
  watch: {
    '$route.params.cashierId': function (newId) {
      this.cashierId = newId
      this.fetchPendingInvoices()
    },
    'pagination.page': function () {
      this.fetchPendingInvoices()
    },
    filterDate() {
      if (this.pagination.page !== 1) {
        this.pagination.page = 1
        return
      }
      this.fetchPendingInvoices()
    },
    filterType() {
      if (this.pagination.page !== 1) {
        this.pagination.page = 1
        return
      }
      this.fetchPendingInvoices()
    },
    filterServiceType() {
      if (this.pagination.page !== 1) {
        this.pagination.page = 1
        return
      }
      this.fetchPendingInvoices()
    },
  },
  mounted() {
    this.cashierId = this.$route.params.cashierId
    this.loadServiceTypes()
    this.fetchPendingInvoices()
  },
  methods: {
    onPageSizeChange() {
      this.pagination.page = 1 // Reset to first page when changing page size
      this.fetchPendingInvoices()
    },
    async loadServiceTypes() {
      try {
        await this.getList('serviceTypes?page=1&take=10&sort=-id')
        const rows = Array.isArray(this.all_data?.data)
          ? this.all_data.data
          : []
        this.serviceTypeOptions = rows.map(r => ({
          id: r.id,
          name: r.name,
        }))
      } catch (e) {
        this.serviceTypeOptions = []
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatAmount(amount, currency = 'IQD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    async fetchPendingInvoices() {
      if (!this.cashierId) return
      this.loading = true
      try {
        let url = `invoices/cashier/${this.cashierId}/pending?page=${this.pagination.page}&take=${this.pagination.pageSize}&sort=-id&include=patientCard,createdBy,paidBy`
        if (this.filterDate) {
          url += `&filter[date]=${this.formatDateForAPI(this.filterDate)}`
        }
        if (this.filterType) {
          url += `&filter[type]=${this.filterType}`
        }
        if (this.filterServiceType) {
          url += `&filter[service_type_id]=${this.filterServiceType}`
        }
        await this.getList(url)
        if (this.all_data) {
          // Normalize data array
          let rows = []
          if (Array.isArray(this.all_data.data)) {
            rows = this.all_data.data
          } else if (Array.isArray(this.all_data)) {
            rows = this.all_data
          }
          this.invoices = rows

          // Handle new pagination structure
          if (this.all_data.pagination) {
            this.pagination.page = this.all_data.pagination.current_page || 1
            this.pagination.pageSize = this.all_data.pagination.per_page || this.pagination.pageSize
            this.pagination.total = this.all_data.pagination.total || 0
            this.pagination.from = this.all_data.pagination.from || null
            this.pagination.to = this.all_data.pagination.to || null
          } else if (
            this.all_data.current_page
            || this.all_data.per_page
            || this.all_data.total
          ) {
            // Fallback to Laravel pagination style
            this.pagination.page = this.all_data.current_page || 1
            this.pagination.pageSize = this.all_data.per_page || this.pagination.pageSize
            this.pagination.total = this.all_data.total || 0
          } else if (this.all_data.meta) {
            // Fallback to meta pagination style
            this.pagination.page = this.all_data.meta.page || 1
            this.pagination.pageSize = this.all_data.meta.take
              || this.all_data.meta.pageSize
              || this.pagination.pageSize
            this.pagination.total = this.all_data.meta.total || 0
          }

          // Handle sums/summary
          if (this.all_data.sums) {
            this.sums = {
              total_amount: this.all_data.sums.total_amount || 0,
              total_discount: this.all_data.sums.total_discount || 0,
              net_amount: this.all_data.sums.net_amount || 0,
              count: this.all_data.sums.count || 0,
            }
          } else {
            // Reset sums if not provided
            this.sums = {
              total_amount: 0,
              total_discount: 0,
              net_amount: 0,
              count: 0,
            }
          }

          if (this.all_data.refunds) {
            this.refunds = {
              total_refund_amount: this.all_data.refunds.total_refund_amount || 0,
              refund_count: this.all_data.refunds.refund_count || 0,
            }
          } else {
            this.refunds = {
              total_refund_amount: 0,
              refund_count: 0,
            }
          }
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('cashiers.failed-fetch-pending'),
          },
        })
      } finally {
        this.loading = false
      }
    },
    async handleBulkApprove() {
      this.loading = true
      try {
        const form = {}
        if (this.approveNote.trim()) {
          form.note = this.approveNote.trim()
        }
        const response = await this.create({
          path: `invoices/cashier/${this.cashierId}/bulk-approve`,
          form,
        })
        this.showApproveModal = false
        this.approveNote = ''
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.success'),
            icon: 'CheckIcon',
            variant: 'success',
            text: this.$t('cashiers.approved-count', {
              count: response.data.approved || 0,
            }),
          },
        })
        // Refresh the list
        await this.fetchPendingInvoices()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message
              || this.$t('cashiers.failed-approve'),
          },
        })
      } finally {
        this.loading = false
      }
    },
    formatDateForAPI(date) {
      if (!date) return null
      if (typeof date === 'string') return date
      const d = new Date(date)
      return d.toISOString().split('T')[0]
    },
    formatInvoiceType(type) {
      if (!type) return '-'
      // Format the key: snake_case to Title Case
      const formattedKey = type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      const translationKey = INVOICE_TYPE_MAP[type]
      if (translationKey) {
        const translated = this.$t(translationKey)
        // If translation exists and is different from key, return both
        if (translated && translated !== translationKey) {
          return `${translated} (${formattedKey})`
        }
      }
      // Fallback: return formatted key only
      return formattedKey
    },
  },
}
</script>

<style scoped>
.text-nowrap {
  white-space: nowrap;
}

.summary-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.5rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  min-width: 200px;
  width: 100%;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.summary-row > [class*='col-'] {
  flex: 1 1 200px;
  max-width: 100%;
}

.summary-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.summary-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
}

.summary-value.net-amount {
  color: #22c55e;
  font-size: 1.5rem;
}

.summary-value.refund-amount {
  color: #dc3545;
}

@media (max-width: 768px) {
  .summary-item {
    padding: 0.75rem;
  }

  .summary-value {
    font-size: 1.125rem;
  }

  .summary-value.net-amount {
    font-size: 1.25rem;
  }
}
</style>
